import React, { useState, useCallback, useEffect } from "react";
import { supabase } from "../../../supabaseClient";
import { useAdmin } from "../../../context/AdminContext";
import { RegistrationRosterModal } from "../modals/RosterModal";
import { useSeason } from "../../../context/SeasonContext";
import { InlineNotice } from "../../../utils/InlineNotice";
import { useInlineNotice } from "../../../hooks/useInlineNotice";
import { InlineConfirm } from "../../../utils/InlineConfirm";

const RegistrationsPanel = () => {
  const { adminId } = useAdmin();
  const { registrationSeason } = useSeason();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [viewingRoster, setViewingRoster] = useState(null);
  const [rosterPlayers, setRosterPlayers] = useState([]);
  const { notice, showError, showSuccess, clear } = useInlineNotice();

  const loadRegistrations = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("registration_submissions")
      .select("*")
      .eq("season_id", registrationSeason?.id)
      .order("submitted_at", { ascending: false });
    setRegistrations(data || []);
    setLoading(false);
  }, [registrationSeason?.id]);

  useEffect(() => {
    loadRegistrations();
  }, [loadRegistrations]);

  const loadRoster = async (registrationId) => {
    const { data } = await supabase
      .from("registration_players")
      .select("*")
      .eq("registration_id", registrationId);
    setRosterPlayers(data || []);
  };

  const openRoster = async (reg) => {
    setViewingRoster(reg);
    await loadRoster(reg.id);
  };

  const approveRegistration = async (reg) => {
    try {
      const { error } = await supabase.functions.invoke("admin-actions", {
        body: {
          action: "approveRegistration",
          adminId,
          payload: { registrationId: reg.id, seasonId: reg.season_id },
        },
      });
      if (error) throw error;
      loadRegistrations();
    } catch (err) {
      showError(`Error approving registration: ${err.message}`);
    }
  };

  const rejectRegistration = async (reg, reason) => {
    try {
      const { error } = await supabase.functions.invoke("admin-actions", {
        body: {
          action: "rejectRegistration",
          adminId,
          payload: { registrationId: reg.id, reason },
        },
      });
      if (error) throw error;
      showSuccess("Registration rejected and email sent.");
      loadRegistrations();
    } catch (err) {
      showError("Error rejecting registration.");
    }
  };

  const pending = registrations.filter((r) => r.status === "pending");
  const history = registrations.filter((r) => r.status !== "pending");

  if (loading)
    return (
      <p className="text-gray-500 dark:text-gray-400 text-center py-8">
        Loading…
      </p>
    );

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
        Team Registrations
      </h2>

      <InlineNotice notice={notice} onDismiss={clear} />

      {pending.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400 text-center py-8">
          No pending registrations
        </p>
      ) : (
        pending.map((reg) => (
          <div
            key={reg.id}
            className="border dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50"
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-orange-600 dark:text-orange-400">
                  {reg.team_name}
                </h3>
                <div className="mt-2 grid grid-cols-2 gap-4 text-sm text-gray-800 dark:text-gray-200">
                  <div>
                    <span className="font-semibold">Captain:</span>{" "}
                    {reg.captain_name}
                  </div>
                  <div>
                    <span className="font-semibold">Email:</span>{" "}
                    {reg.captain_email}
                  </div>
                  <div>
                    <span className="font-semibold">Phone:</span>{" "}
                    {reg.captain_phone || "N/A"}
                  </div>
                  <div>
                    <span className="font-semibold">Players:</span>{" "}
                    {reg.num_players}
                    <br />
                    <button
                      onClick={() => openRoster(reg)}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 mt-1 text-sm"
                    >
                      View Roster
                    </button>
                  </div>
                  <div>
                    <span className="font-semibold">Total Cost:</span> $
                    {reg.total_cost}
                  </div>
                  <div>
                    <span className="font-semibold">Payment:</span>{" "}
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        reg.payment_status === "paid"
                          ? "bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300"
                          : "bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300"
                      }`}
                    >
                      {reg.payment_status}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold">Ref Fee:</span>{" "}
                    {reg.ref_fee_coverage ? "Yes" : "No"}
                  </div>
                  <div>
                    <span className="font-semibold">Submitted:</span>{" "}
                    {new Date(reg.submitted_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <div className="ml-4 flex flex-col gap-2 items-end">
                <button
                  onClick={() => approveRegistration(reg)}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 whitespace-nowrap w-full"
                >
                  ✓ Approve & Create Team
                </button>
                <InlineConfirm
                  triggerLabel="✗ Reject"
                  triggerClassName="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 whitespace-nowrap w-full"
                  message={`Reject registration for ${reg.team_name}?`}
                  confirmLabel="Reject"
                  requireReason
                  reasonPlaceholder="Reason (emailed to the captain)"
                  onConfirm={(reason) => rejectRegistration(reg, reason)}
                />
              </div>
            </div>
          </div>
        ))
      )}

      {history.length > 0 && (
        <div className="mt-8">
          <button
            onClick={() => setIsHistoryOpen(!isHistoryOpen)}
            className="flex justify-between items-center w-full text-left mb-4"
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              Registration History
            </h3>
            <span
              className={`transform transition-transform ${isHistoryOpen ? "rotate-180" : ""}`}
            >
              ▼
            </span>
          </button>
          {isHistoryOpen && (
            <div className="space-y-2">
              {history.map((reg) => (
                <div
                  key={reg.id}
                  className="border dark:border-gray-600 rounded p-3 text-sm flex justify-between items-center"
                >
                  <div>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {reg.team_name}
                    </span>
                    <span className="text-gray-600 dark:text-gray-400 ml-2">
                      - {reg.captain_name}
                    </span>
                  </div>
                  <span
                    className={`px-3 py-1 rounded text-xs font-semibold ${
                      reg.status === "approved"
                        ? "bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300"
                        : "bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300"
                    }`}
                  >
                    {reg.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {viewingRoster && (
        <RegistrationRosterModal
          registration={viewingRoster}
          players={rosterPlayers}
          onClose={() => setViewingRoster(null)}
          onRefresh={() => loadRoster(viewingRoster.id)}
        />
      )}
    </div>
  );
};

export default RegistrationsPanel;
